// src/hooks/useAnalysis.js
import { useState, useEffect } from 'react';
import { apiService } from '../services/api';

export const useAnalysis = (sha256) => {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [canRescan, setCanRescan] = useState(false);
  const [canDownload, setCanDownload] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Fetch analysis data
  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/files/analysis/${sha256}`);
      setAnalysisData(response);
    } catch (err) {
      console.error('Error fetching analysis:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Check bookmark status
  const checkBookmarkStatus = async () => {
    try {
      const response = await fetch(`${apiService.baseUrl}/files/bookmarks`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bookmarks');
      }

      const bookmarks = await response.json();
      const bookmarkExists = bookmarks.some(bookmark => 
        bookmark.sha256 === sha256
      );
      
      // Set bookmark state and store in localStorage
      setIsBookmarked(bookmarkExists);
      localStorage.setItem(`bookmark_${sha256}`, bookmarkExists.toString());
    } catch (error) {
      console.error('Error checking bookmark status:', error);
      
      // Fallback to localStorage if network request fails
      const storedBookmark = localStorage.getItem(`bookmark_${sha256}`);
      setIsBookmarked(storedBookmark === 'true');
    }
  };

  // Check if file is available for rescanning
  const checkRescanAvailability = async () => {
    try {
      const response = await fetch(`${apiService.baseUrl}/files/rescan/${sha256}/check`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to check rescan availability');
      }
      
      const data = await response.json();
      setCanRescan(data.can_rescan);
    } catch (error) {
      console.error('Error checking rescan availability:', error);
      setCanRescan(false);
    }
  };

  // Toggle bookmark
  const toggleBookmark = async () => {
    try {
      const method = isBookmarked ? 'DELETE' : 'POST';
      const response = await fetch(`${apiService.baseUrl}/files/bookmark/${sha256}`, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to update bookmark');
      }

      // Toggle local state and update localStorage
      const newBookmarkState = !isBookmarked;
      setIsBookmarked(newBookmarkState);
      localStorage.setItem(`bookmark_${sha256}`, newBookmarkState.toString());
    } catch (error) {
      console.error('Bookmark error:', error);
      
      // Optionally, show an error message to the user
      alert('Failed to update bookmark. Please try again.');
    }
  };

  // Handle rescan action
  const handleRescan = async () => {
    if (!canRescan) return false;
    
    try {
      const response = await fetch(`${apiService.baseUrl}/files/rescan/${sha256}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        throw new Error('Failed to initiate rescan');
      }
      
      await response.json();
      return true;
    } catch (error) {
      console.error('Rescan error:', error);
      return false;
    }
  };

  const checkDownloadAvailability = async () => {
  try {
    const token = localStorage.getItem('token');
    // Use the same URL pattern as your other functions
    const response = await fetch(`${apiService.baseUrl}/files/download/${sha256}/check`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('Download availability check:', data);
      setCanDownload(data.can_download);
    } else {
      console.error('Failed to check download availability:', response.status);
      setCanDownload(false);
    }
  } catch (error) {
    console.error('Error checking download availability:', error);
    setCanDownload(false);
  }
};

const handleDownload = async () => {
  console.log('handleDownload called');
  console.log('canDownload:', canDownload, 'isDownloading:', isDownloading);
  
  if (!canDownload || isDownloading) {
    console.log('Download blocked');
    return;
  }
  
  setIsDownloading(true);
  try {
    const token = localStorage.getItem('token');
    
    // Use the same URL pattern as your other functions - with full localhost URL
    const url = `${apiService.baseUrl}/files/download/${sha256}`;
    console.log('Download URL:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'  // You can remove this for file downloads
      }
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Download failed:', errorText);
      throw new Error(`Download failed: ${response.status}`);
    }

    // Check content type
    const contentType = response.headers.get('content-type');
    console.log('Content-Type:', contentType);
    
    if (contentType && contentType.includes('text/html')) {
      const htmlContent = await response.text();
      console.error('Got HTML instead of file:', htmlContent.substring(0, 200));
      throw new Error('Server returned HTML instead of file');
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `${sha256.substring(0, 8)}.apk`;
    
    if (contentDisposition) {
      console.log('Content-Disposition:', contentDisposition);
      // Improved filename parsing
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    console.log('Using filename:', filename);

    // Create blob and download
    const blob = await response.blob();
    console.log('Blob created - size:', blob.size, 'type:', blob.type);
    
    if (blob.size === 0) {
      throw new Error('Downloaded file is empty');
    }

    // Create download link
    const url_blob = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url_blob;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    setTimeout(() => {
      window.URL.revokeObjectURL(url_blob);
      if (document.body.contains(a)) {
        document.body.removeChild(a);
      }
    }, 100);

    console.log('Download completed successfully');

  } catch (error) {
    console.error('Download error:', error);
    throw error;
  } finally {
    setIsDownloading(false);
  }

};

  // Initial data fetching and status checking
  useEffect(() => {
    if (sha256) {
      fetchAnalysis();
      checkBookmarkStatus();
      checkRescanAvailability();
      checkDownloadAvailability();
    }
  }, [sha256]);

  return {
    analysisData,
    loading,
    error,
    isBookmarked,
    canRescan,
    canDownload,
    isDownloading,
    toggleBookmark,
    handleRescan,
    handleDownload,
    refetch: fetchAnalysis
  };
};